import React, { useState } from "react";
import { Button, Input, InputAdornment } from "@mui/material";
import { ExpressionValue } from "../../rule-engine-core/Expressions/ExpressionValue";
import {
    ExpressionRuleRegistration,
    ExpressionRuleRegistrationData,
} from "../../rule-engine-core/Expressions/RegisterExpressionRule";
import styled from "styled-components";
import { ExpressionsGrid } from "./CommonUI";
import { SearchIcon } from "../../CommonUI/Icon/SearchIcon";
import { AddExpressionButton } from "./AddExpressionButton";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { Box } from "@mui/material";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export const ToolboxPanel = function (props: {
    large: boolean;
    expressionValue: ExpressionValue;
    onExpressionSelected: (registration: ExpressionRuleRegistrationData) => void;
}) {
    const [selectedCategory, setSelectedCategory] = useState<string>("Common");
    const [search, setSearch] = useState<string>("");

    const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    let expressionCategories = [
        "All",
        ...ExpressionRuleRegistration.getCategories(props.expressionValue.editContext!.expression.getInputDefinition()),
    ];

    return (
        <ToolboxPage>
            <ToolboxPanelSideBar>
                <ToolboxPanelSearchInput
                    value={search}
                    disableUnderline
                    onChange={onChangeSearch}
                    placeholder="Filter"
                    endAdornment={
                        <InputAdornment position="end">
                            <SearchIcon />
                        </InputAdornment>
                    }
                />

                <ToolboxTabs>
                    <label>Categories</label>
                    <StyledTabList>
                        <Button
                            onClick={() => setSelectedCategory("Common")}
                            classes={{ root: selectedCategory === "Common" ? "Mui-selected" : undefined }}
                        >
                            Common
                        </Button>
                        {expressionCategories.map((category) => {
                            return (
                                <Button
                                    onClick={() => setSelectedCategory(category)}
                                    key={category}
                                    classes={{ root: selectedCategory === category ? "Mui-selected" : undefined }}
                                >
                                    {category}
                                </Button>
                            );
                        })}
                    </StyledTabList>
                </ToolboxTabs>
            </ToolboxPanelSideBar>
            <ToolboxPanelContent>
                {selectedCategory === "All" && (
                    <ToolboxTabPanel>
                        <ExpressionsGrid columnCount={3}>
                            {ExpressionRuleRegistration.getAllExpressions(
                                props.expressionValue.editContext!.expression.getInputDefinition(),
                            )
                                .filter((r) => {
                                    return IncludeItem(r.name);
                                })
                                .map((value: ExpressionRuleRegistrationData, index: number) => (
                                    <AddExpressionButton
                                        expression={props.expressionValue}
                                        registration={value}
                                        hideLabel={false}
                                        key={"regitem" + index}
                                        showCategory={true}
                                        onClick={(registration: ExpressionRuleRegistrationData) => {
                                            props.onExpressionSelected(registration);
                                        }}
                                    />
                                ))}
                        </ExpressionsGrid>
                    </ToolboxTabPanel>
                )}
                {selectedCategory === "Common" && (
                    <ToolboxTabPanel>
                        <ExpressionsGrid columnCount={3}>
                            {ExpressionRuleRegistration.getCommonExpressions(
                                props.expressionValue.editContext!.expression.getInputDefinition(),
                            )
                                .filter((r) => {
                                    return IncludeItem(r.name);
                                })
                                .map((value: ExpressionRuleRegistrationData, index: number) => (
                                    <AddExpressionButton
                                        expression={props.expressionValue}
                                        registration={value}
                                        hideLabel={false}
                                        key={"regitem" + index}
                                        showCategory={true}
                                        onClick={(registration: ExpressionRuleRegistrationData) => {
                                            props.onExpressionSelected(registration);
                                        }}
                                    />
                                ))}
                        </ExpressionsGrid>
                    </ToolboxTabPanel>
                )}
                {expressionCategories
                    .filter((c) => selectedCategory === c)
                    .map((category) => {
                        let c = ExpressionRuleRegistration.getExpressionsByCategory(
                            category,
                            props.expressionValue.editContext!.expression.getInputDefinition(),
                        ).filter((r) => {
                            return IncludeItem(r.name);
                        });
                        return (
                            <ToolboxTabPanel key={category}>
                                <ExpressionsGrid columnCount={3}>
                                    {c.map((value: ExpressionRuleRegistrationData, index: number) => (
                                        <AddExpressionButton
                                            expression={props.expressionValue}
                                            registration={value}
                                            hideLabel={false}
                                            key={"regitem" + index}
                                            onClick={(registration: ExpressionRuleRegistrationData) => {
                                                props.onExpressionSelected(registration);
                                            }}
                                        />
                                    ))}
                                </ExpressionsGrid>
                            </ToolboxTabPanel>
                        );
                    })}
            </ToolboxPanelContent>
        </ToolboxPage>
    );

    function IncludeItem(name: string): boolean {
        if (search && search.trim() !== "") {
            return name.toLowerCase().indexOf(search.toLowerCase()) !== -1;
        }
        return true;
    }
};

const ToolboxPage = styled.div`
    display: flex;
    float: left;
    width: 100%;
    flex-wrap: wrap;
    padding: 16px 0;
    max-height: 376px;
    align-content: flex-start;
    flex-direction: row;
    overflow: hidden;
`;

const ToolboxPanelSideBar = styled.div`
    width: 144px;
    margin-right: 12px;

    > * {
        margin-top: 10px;

        &:first-child {
            margin-top: 0px;
        }
    }
`;

const ToolboxPanelContent = styled.div`
    flex: 1;
    padding-left: 12px;
    border-left: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    height: 300px;
`;

const ToolboxPanelSearchInput = styled(Input)`
    && {
        padding: 6px 12px;

        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 140%;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};

        background: ${UIStyleConstants.WHITE_COLOR};

        border-radius: 4px;

        &::before {
            content: "";
            display: none;
        }

        .MuiInputBase-input {
            padding: 0;
        }
    }
`;

function ToolboxTabPanel(props: { children: AppGlueAny }) {
    return (
        <Box
            padding={0}
            height={"300px"}
            overflow={"auto"}
        >
            {props.children}
        </Box>
    );
}

// = styled.div`
//     padding: 0;
//     overflow: auto;
//     height: 300px;
// `;

const ToolboxTabs = styled.div`
    padding: 6px 0 0;

    > label {
        font-size: 10px;
        font-style: normal;
        font-weight: 500;
        line-height: 12px;
        letter-spacing: 0.20000000298023224px;
        text-align: left;
        color: ${UIStyleConstants.BLUE_COLOR};

        margin-bottom: 10px;
        display: block;
    }
`;

const StyledTabList = styled.div`
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    border-radius: 4px;
    padding: 2px;

    .MuiTabs-indicator {
        display: none;
    }

    .MuiButtonBase-root {
        min-width: auto;
        width: 100%;
        padding: 8px 16px;

        font-size: 11px;
        font-style: normal;
        font-weight: 500;
        line-height: 13px;
        letter-spacing: 0em;

        border-radius: 4px;

        display: flex;
        align-items: center;
        justify-content: start;
        color: ${UIStyleConstants.BLUE_COLOR};

        text-transform: none;

        min-height: 0;

        &.Mui-selected {
            background: ${UIStyleConstants.WHITE_COLOR};
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        }

        .MuiTab-wrapper {
            display: block;
            text-align: left;
        }
    }
`;
